import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { QuillModule } from 'ngx-quill';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  MatChipsModule,
  MatChipGrid,
  MatChipRow,
  MatChipInput,
  MatChipInputEvent,
} from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { Router, ActivatedRoute } from '@angular/router';
import { BlobServiceClient } from '@azure/storage-blob';

import { Inputfiledsbase } from '../../../inputfileds/models/inputfiledsbase';
import { DynamicFormQuestionComponent } from '../../../inputfileds/dynamic-form-question/dynamic-form-question.component';
import { QuestionControlService } from '../../../inputfileds/form_services/question-control.service';
import { CareersService } from '../../../inputfileds/form_services/careers/careers.service';
import { environment } from '../../../../environments/environment';
import { ConnectiveservicesService } from '../../../Services/Apis/Connectiveservices/connectiveservices.service';
import { ApiservicesService } from '../../../Services/Apis/apiservices.service';
import { ToastService } from '../../../Services/toast/toast.service';
import { TranslateDirective } from '../../../components/Utilites/Directives/translate.directive';
import { ConfigService } from '../../../Services/configservices/sasTokenConfig';

@Component({
  selector: 'app-careerpageview',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonToggleModule,
    DynamicFormQuestionComponent,
    QuillModule,
    MatChipsModule,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatTableModule,
    MatMenuModule,
    TranslateDirective,
  ],
  templateUrl: './careerpageview.component.html',
  styleUrls: ['./careerpageview.component.scss'],
  providers: [QuestionControlService, CareersService],
})
export class CareerpageviewComponent implements OnInit {
  @Input() apiEndPoint!: string;
  @Input() websiteId!: string;
  @Input() pageId!: string;

  private config = inject(ConfigService);

  questions: Inputfiledsbase<string>[] = [];
  form!: FormGroup;
  showForm = false;
  skillList: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  fileUrl: string | null = null;
  htmlText = '';
  containerName = environment.ASSET_CONTAINER;

  CareersList: any[] = [];
  displayedColumns: string[] = [
    'position_name',
    'department_name',
    'number_of_openings',
    'experience_in_years',
    'status',
    'actions',
  ];

  currentEditId: number | null = null;
  isEditMode = false;

  quillConfig = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['clean'],
        ['link', 'image', 'video'],
      ],
    },
  };

  constructor(
    private qcs: QuestionControlService,
    private fb: FormBuilder,
    private router: Router,
    private CareersQuestionService: CareersService,
    private connectiveservices: ConnectiveservicesService,
    private apiservice: ApiservicesService,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {
    this.CareersQuestionService.getJobPostingform().subscribe(
      (q) => (this.questions = q),
    );
  }

  ngOnInit(): void {
    this.websiteId = this.route.snapshot?.queryParams['websiteId'];
    this.pageId = this.route.snapshot?.queryParams['pageId'];
    this.apiEndPoint = this.route.snapshot?.queryParams['endpoint'];

    this.initializeForm();
    this.getdata();
  }

  initializeForm(): void {
    this.form = this.qcs.toFormGroup(this.questions);
    this.form.addControl('company_profile', this.fb.control(''));
    this.form.addControl('is_published', this.fb.control(false));
    this.form.addControl('jd_url', this.fb.control('', Validators.required));
  }

  getdata(): void {
    this.apiservice.getCareersList(this.websiteId, this.pageId).subscribe({
      next: (res: any) => {
        this.CareersList = res?.rows || res?.data || [];
      },
      error: (err) => console.error('Error fetching careers:', err),
    });
  }

  async editCareersData(id: number): Promise<void> {
    this.isEditMode = true;
    this.currentEditId = id;

    const itemToEdit = this.CareersList.find((item) => item.id === id);
    if (!itemToEdit) return;

    this.showForm = true;

    this.form.patchValue({
      position_name: itemToEdit.position_name,
      department_id: itemToEdit.department_id,
      employment_type_id: itemToEdit.employment_type_id,
      salary: itemToEdit.salary,
      number_of_openings: itemToEdit.number_of_openings,
      experience_in_years: itemToEdit.experience_in_years,
      mode_of_work: itemToEdit.mode_of_work,
      location: itemToEdit.location,
      company_profile: itemToEdit.company_profile,
      phone_number: itemToEdit.phone_number,
      email: itemToEdit.email,
      status: itemToEdit.is_published,
    });

    if (itemToEdit.skills?.skills) {
      this.skillList = [...itemToEdit.skills.skills];
    }

    try {
      if (itemToEdit.jd_url) {
        // Get fresh connection info
        const connectionString = await this.config.getStorageConnection();
        const baseUrl = connectionString.split('?')[0];

        const fullUrl = `${baseUrl}${this.containerName}/${itemToEdit.jd_url}`;
        const fileContent = await this.fetchFileContent(fullUrl);
        const formattedContent = this.formatTextForQuill(fileContent);

        this.form.get('jd_url')?.setValue(formattedContent);
        this.htmlText = formattedContent;
      }
    } catch (error) {
      console.error('Error loading JD content:', error);
      this.form.get('jd_url')?.setValue('');
    }
  }

  private async fetchFileContent(fullUrl: string): Promise<string> {
    try {
      const connectionString = await this.config.getStorageConnection();
      const blobServiceClient = new BlobServiceClient(connectionString);

      const url = new URL(fullUrl);
      const blobName = url.pathname.split('/').slice(2).join('/');

      const containerClient = blobServiceClient.getContainerClient(
        this.containerName,
      );
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);

      const downloadResponse = await blockBlobClient.download();
      const blobText = await downloadResponse.blobBody?.then(async (blob) => {
        return await new Response(blob).text();
      });

      return blobText || '';
    } catch (error) {
      console.error('Error fetching file content:', error);
      throw error;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) return;

    try {
      // 1. Force a fresh token before upload
      this.config.clearCache();
      const connectionString = await this.config.getStorageConnection();
      // connectionString now looks like: https://...blob.core.windows.net/?sv=...

      const formData = this.form.value;
      const cleanFileName = this.generateBlobName();
      const blobContent = this.getSanitizedJobDescription(this.htmlText);
      const blob = new Blob([blobContent], { type: 'text/plain' });
      const file = new File([blob], cleanFileName, { type: 'text/plain' });

      // 2. Perform the upload
      // Ensure connectiveservices.uploadFile is using the fresh SAS connection
      const urlWithSAS = await this.connectiveservices.uploadFile(
        file,
        Number(this.websiteId),
        'CAREERS',
      );

      // 3. LOG THIS: If this log doesn't show "?sv=...", the upload will fail
      console.log('Upload attempted with URL:', urlWithSAS);

      const url = urlWithSAS.split('?')[0];
      const path = new URL(url).pathname;
      this.fileUrl = path.split('/').slice(2).join('/');

      this.toast.showToast('success', 'File uploaded successfully!');

      const submissionData = {
        ...formData,
        skills: { skills: this.skillList },
        jd_url: this.fileUrl,
        website_id: this.websiteId,
        website_page_id: this.pageId,
      };

      if (this.isEditMode && this.currentEditId) {
        await this.updateCareer(this.currentEditId, submissionData);
      } else {
        await this.createCareer(submissionData);
      }

      this.resetForm();
      this.getdata();
      this.showForm = false;
      this.toast.showToast('success', 'Career saved successfully!');
    } catch (error: any) {
      console.error('Submission error:', error);
      this.toast.showToast(
        'danger',
        'Upload failed. Check console for 401 details.',
      );
    }
  }

  private formatTextForQuill(text: string): string {
    let htmlContent = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return htmlContent
      .split('\n')
      .map((p) => (p.trim() ? `<p>${p}</p>` : '<p><br></p>'))
      .join('');
  }

  private getSanitizedJobDescription(htmlContent: string = ''): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const breaks = tempDiv.querySelectorAll('p, br, li');
    breaks.forEach((el) => {
      const br = document.createTextNode('\n');
      el.parentNode?.insertBefore(br, el.nextSibling);
    });
    return tempDiv.textContent?.trim() || '';
  }

  private generateBlobName(): string {
    const position = this.form.get('position_name')?.value || 'Position';
    const sanitizedPosition = position
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_]/g, '');
    const currentDate = new Date().toISOString().split('T')[0];
    return `${sanitizedPosition}_${currentDate}.txt`;
  }

  private resetForm(): void {
    this.form.reset();
    this.skillList = [];
    this.htmlText = '';
    this.currentEditId = null;
    this.isEditMode = false;
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  addSkill(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value && !this.skillList.includes(value)) {
      this.skillList.push(value);
    }
    event.chipInput?.clear();
  }

  removeSkill(skill: string): void {
    const index = this.skillList.indexOf(skill);
    if (index >= 0) this.skillList.splice(index, 1);
  }

  editSkill(skill: string, event: any): void {
    const value = event.value.trim();
    const index = this.skillList.indexOf(skill);
    if (!value) {
      this.removeSkill(skill);
    } else if (index >= 0) {
      this.skillList[index] = value;
    }
  }

  onContentChanged = (event: any) => {
    if (event.source === 'user') this.htmlText = event.html;
  };

  viewCareerDetails(id: string): void {
    this.router.navigate([`websites/WebcomponentList/careers`, id]);
  }

  deleteCareers(id: number): void {
    this.apiservice.deleteCareerData(id).subscribe({
      next: (res) => {
        if (res.code === 200 && res.status === true) {
          this.getdata();
        }
      },
      error: (err) => console.error('Delete error:', err),
    });
  }

  private async createCareer(data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apiservice.createCareers(data).subscribe({
        next: (res: any) =>
          res.code === 200 ? resolve() : reject(res.message),
        error: (err) => reject(err),
      });
    });
  }

  private async updateCareer(id: number, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apiservice.updateCareerData(id, data).subscribe({
        next: (res: any) =>
          res.code === 200 ? resolve() : reject(res.message),
        error: (err) => reject(err),
      });
    });
  }

  openJobForm(): void {
    this.resetForm();
    this.showForm = true;
  }

  navigateBack(): void {
    if (this.showForm) {
      this.resetForm();
      this.showForm = false;
    } else {
      this.router.navigate([`/websites/WebcomponentList`], {
        queryParams: { websiteId: this.websiteId, pageId: this.pageId },
      });
    }
  }

  onSelectionChanged = (event: any) => {
    if (event.oldRange == null) this.onFocus();
    if (event.range == null) this.onBlur();
  };

  updateCareerStatus(item: any): void {
    const newStatus = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    this.apiservice
      .updateCareerData(item.id, { ...item, status: newStatus })
      .subscribe({
        next: (response: any) => {
          this.getdata();
        },
        error: (err) => console.error('Error updating status:', err),
      });
  }

  onFocus = () => console.log('On Focus');
  onBlur = () => console.log('Blurred');

  handleApplicantView(id: any) {
    this.router.navigate([`websites/WebcomponentList/careers/applicants`], {
      queryParams: { websiteId: this.websiteId, pageId: this.pageId },
    });
  }
}
